import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdCloudUpload, MdDownload, MdHistory } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import {
    fetchDistributorImports,
    fetchDistributorExports,
    createDistributorImport,
    createDistributorExport,
} from '../../../../services/Http/DistributorApi';

const importTypes = ['sku', 'warranty', 'retailer'];

const DistributorImportExport = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeTab, setActiveTab] = useState('imports');
    const [imports, setImports] = useState([]);
    const [exports, setExports] = useState([]);
    const [importForm, setImportForm] = useState({
        import_type: 'sku',
        source_name: '',
        source_url: '',
    });
    const [exportForm, setExportForm] = useState({
        export_type: 'sku',
        file_name: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const [importsResponse, exportsResponse] = await Promise.all([
                fetchDistributorImports(),
                fetchDistributorExports(),
            ]);
            setImports(importsResponse.data || []);
            setExports(exportsResponse.data || []);
        } catch (err) {
            console.error(err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleImportSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        try {
            await createDistributorImport(importForm);
            toast.success(t('DistributorImportExport.toast.importCreated'));
            setImportForm({ ...importForm, source_name: '', source_url: '' });
            await loadData();
        } catch (err) {
            console.error(err);
            toast.error(t('DistributorImportExport.toast.error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleExportSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        try {
            await createDistributorExport(exportForm);
            toast.success(t('DistributorImportExport.toast.exportCreated'));
            setExportForm({ ...exportForm, file_name: '' });
            await loadData();
        } catch (err) {
            console.error(err);
            toast.error(t('DistributorImportExport.toast.error'));
        } finally {
            setSubmitting(false);
        }
    };

    const latestImportsByType = useMemo(() => {
        const grouped = {};
        imports.forEach((item) => {
            if (!grouped[item.import_type] || new Date(item.created_at) > new Date(grouped[item.import_type].created_at)) {
                grouped[item.import_type] = item;
            }
        });
        return grouped;
    }, [imports]);

    const latestExportsByType = useMemo(() => {
        const grouped = {};
        exports.forEach((item) => {
            if (!grouped[item.export_type] || new Date(item.created_at) > new Date(grouped[item.export_type].created_at)) {
                grouped[item.export_type] = item;
            }
        });
        return grouped;
    }, [exports]);

    if (loading) {
        return <LoadingMessage>{t('DistributorImportExport.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('DistributorImportExport.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <TitleGroup>
                    <PageTitle>{t('DistributorImportExport.title')}</PageTitle>
                    <Subtitle>{t('DistributorImportExport.subtitle')}</Subtitle>
                </TitleGroup>
                <Tabs>
                    <TabButton type="button" $active={activeTab === 'imports'} onClick={() => setActiveTab('imports')}>
                        <MdCloudUpload /> {t('DistributorImportExport.tabs.imports')}
                    </TabButton>
                    <TabButton type="button" $active={activeTab === 'exports'} onClick={() => setActiveTab('exports')}>
                        <MdDownload /> {t('DistributorImportExport.tabs.exports')}
                    </TabButton>
                </Tabs>
            </PageHeader>
            <PageContent>
                {activeTab === 'imports' ? (
                    <>
                        <Section>
                            <SectionHeader>
                                <MdCloudUpload />
                                <div>
                                    <SectionTitle>{t('DistributorImportExport.imports.requestTitle')}</SectionTitle>
                                    <SectionSubtitle>{t('DistributorImportExport.imports.requestSubtitle')}</SectionSubtitle>
                                </div>
                            </SectionHeader>
                            <Form onSubmit={handleImportSubmit}>
                                <label>{t('DistributorImportExport.form.type')}</label>
                                <select
                                    value={importForm.import_type}
                                    onChange={(event) => setImportForm((prev) => ({ ...prev, import_type: event.target.value }))}
                                >
                                    {importTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {t(`DistributorImportExport.types.${type}`)}
                                        </option>
                                    ))}
                                </select>
                                <label>{t('DistributorImportExport.form.sourceName')}</label>
                                <input
                                    type="text"
                                    value={importForm.source_name}
                                    onChange={(event) => setImportForm((prev) => ({ ...prev, source_name: event.target.value }))}
                                />
                                <label>{t('DistributorImportExport.form.sourceUrl')}</label>
                                <input
                                    type="url"
                                    value={importForm.source_url}
                                    onChange={(event) => setImportForm((prev) => ({ ...prev, source_url: event.target.value }))}
                                />
                                <PrimaryButton type="submit" disabled={submitting}>
                                    {submitting ? t('DistributorImportExport.actions.submitting') : t('DistributorImportExport.actions.startImport')}
                                </PrimaryButton>
                            </Form>
                        </Section>

                        <HistorySection>
                            <HistoryHeader>
                                <MdHistory /> {t('DistributorImportExport.history.imports')}
                            </HistoryHeader>
                            <HistoryTable>
                                <thead>
                                    <tr>
                                        <th>{t('DistributorImportExport.table.type')}</th>
                                        <th>{t('DistributorImportExport.table.source')}</th>
                                        <th>{t('DistributorImportExport.table.status')}</th>
                                        <th>{t('DistributorImportExport.table.progress')}</th>
                                        <th>{t('DistributorImportExport.table.date')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {imports.slice(0, 20).map((item) => (
                                        <tr key={item.id}>
                                            <td>{t(`DistributorImportExport.types.${item.import_type}`)}</td>
                                            <td>{item.source_name || '—'}</td>
                                            <td>{t(`DistributorImportExport.status.${item.status}`)}</td>
                                            <td>
                                                {item.processed_rows ?? 0}/{item.total_rows ?? '—'}
                                            </td>
                                            <td>{new Date(item.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </HistoryTable>
                        </HistorySection>
                    </>
                ) : (
                    <>
                        <Section>
                            <SectionHeader>
                                <MdDownload />
                                <div>
                                    <SectionTitle>{t('DistributorImportExport.exports.requestTitle')}</SectionTitle>
                                    <SectionSubtitle>{t('DistributorImportExport.exports.requestSubtitle')}</SectionSubtitle>
                                </div>
                            </SectionHeader>
                            <Form onSubmit={handleExportSubmit}>
                                <label>{t('DistributorImportExport.form.type')}</label>
                                <select
                                    value={exportForm.export_type}
                                    onChange={(event) => setExportForm((prev) => ({ ...prev, export_type: event.target.value }))}
                                >
                                    {importTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {t(`DistributorImportExport.types.${type}`)}
                                        </option>
                                    ))}
                                </select>
                                <label>{t('DistributorImportExport.form.fileName')}</label>
                                <input
                                    type="text"
                                    value={exportForm.file_name}
                                    onChange={(event) => setExportForm((prev) => ({ ...prev, file_name: event.target.value }))}
                                />
                                <PrimaryButton type="submit" disabled={submitting}>
                                    {submitting ? t('DistributorImportExport.actions.submitting') : t('DistributorImportExport.actions.startExport')}
                                </PrimaryButton>
                            </Form>
                        </Section>

                        <HistorySection>
                            <HistoryHeader>
                                <MdHistory /> {t('DistributorImportExport.history.exports')}
                            </HistoryHeader>
                            <HistoryTable>
                                <thead>
                                    <tr>
                                        <th>{t('DistributorImportExport.table.type')}</th>
                                        <th>{t('DistributorImportExport.table.file')}</th>
                                        <th>{t('DistributorImportExport.table.status')}</th>
                                        <th>{t('DistributorImportExport.table.date')}</th>
                                        <th>{t('DistributorImportExport.table.download')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exports.slice(0, 20).map((item) => (
                                        <tr key={item.id}>
                                            <td>{t(`DistributorImportExport.types.${item.export_type}`)}</td>
                                            <td>{item.file_name || '—'}</td>
                                            <td>{t(`DistributorImportExport.status.${item.status}`)}</td>
                                            <td>{new Date(item.created_at).toLocaleString()}</td>
                                            <td>
                                                {item.file_url ? (
                                                    <a href={item.file_url} target="_blank" rel="noreferrer">
                                                        {t('DistributorImportExport.table.download')}
                                                    </a>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </HistoryTable>
                        </HistorySection>
                    </>
                )}

                <LatestSummary>
                    <h4>{t('DistributorImportExport.history.imports')}</h4>
                    <SummaryGrid>
                        {importTypes.map((type) => (
                            <SummaryCard key={type}>
                                <label>{t(`DistributorImportExport.types.${type}`)}</label>
                                <strong>{latestImportsByType[type]?.status ? t(`DistributorImportExport.status.${latestImportsByType[type].status}`) : t('common.none')}</strong>
                            </SummaryCard>
                        ))}
                    </SummaryGrid>
                </LatestSummary>

                <LatestSummary>
                    <h4>{t('DistributorImportExport.history.exports')}</h4>
                    <SummaryGrid>
                        {importTypes.map((type) => (
                            <SummaryCard key={`export-${type}`}>
                                <label>{t(`DistributorImportExport.types.${type}`)}</label>
                                <strong>{latestExportsByType[type]?.status ? t(`DistributorImportExport.status.${latestExportsByType[type].status}`) : t('common.none')}</strong>
                            </SummaryCard>
                        ))}
                    </SummaryGrid>
                </LatestSummary>
            </PageContent>
        </PageContainer>
    );
};

export default DistributorImportExport;

const TitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Tabs = styled.div`
    display: flex;
    gap: 0.75rem;
`;

const TabButton = styled.button`
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 999px;
    cursor: pointer;
    background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 1)' : 'rgba(130, 22, 146, 0.08)')};
    color: ${({ $active }) => ($active ? '#fff' : 'rgba(130, 22, 146, 1)')};
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
`;

const Section = styled.section`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
`;

const SectionHeader = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1rem;

    svg {
        font-size: 1.4rem;
    }
`;

const SectionTitle = styled.h3`
    margin: 0;
`;

const SectionSubtitle = styled.p`
    margin: 0.25rem 0 0 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Form = styled.form`
    display: grid;
    gap: 0.75rem;

    input, select {
        padding: 0.625rem 0.75rem;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.1);
    }
`;

const HistorySection = styled.section`
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    margin-bottom: 1.5rem;
`;

const HistoryHeader = styled.div`
    padding: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: 600;
`;

const HistoryTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        padding: 0.75rem 1rem;
        text-align: left;
    }

    thead {
        background: rgba(130, 22, 146, 0.05);
    }

    tbody tr {
        border-top: 1px solid rgba(0, 0, 0, 0.04);
    }
`;

const LatestSummary = styled.section`
    margin-top: 1.5rem;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.75rem;
`;

const SummaryCard = styled.div`
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 1rem;

    label {
        display: block;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        margin-bottom: 0.25rem;
    }

    strong {
        font-size: 1.2rem;
    }
`;

const PrimaryButton = styled.button`
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border: none;
    border-radius: 999px;
    padding: 0.625rem 1.25rem;
    cursor: pointer;
`;
